import type { ArchitecturalMetrics } from "src/types/ArchitecturalMetrics";
import { Table } from "../Table";
import { useMemo } from "react";
import { useTranslation, floorLabel } from "src/translations";

export function EfficiencyTable(props: { metrics: ArchitecturalMetrics }) {
  const { t } = useTranslation();
  const rows = useRows(props.metrics, t);
  return (
    <div>
      <Table.Sticky>
        <Table.Head label={t("metrics.efficiency")} variant="section" />
        <Table.Row>
          <Table.Head label={t("metrics.level")} variant="header" position="start" />
          <Table.Head label={t("metrics.efficiency")} variant="header" position="end" />
          <Table.Head label={t("metrics.blocksUnits")} variant="header" position="end" />
        </Table.Row>
      </Table.Sticky>
      {rows.map((row) => (
        <Table.Row key={row.label}>
          <Table.Col>
            <Table.Label label={row.label} variant="cell" />
          </Table.Col>
          <Table.Col>
            <Table.Cell value={row.efficiency} precision={1} variant="cell" symbol="%" />
          </Table.Col>
          <Table.Col>
            <Table.Cell value={row.units} variant="cell" />
          </Table.Col>
        </Table.Row>
      ))}
      <Table.Row>
        <Table.Col>
          <Table.Label label={t("design.residentialEfficiency")} variant="subtotal" />
        </Table.Col>
        <Table.Col>
          <Table.Cell
            value={props.metrics.ResidentialEfficiencyTotal * 100}
            precision={1}
            variant="subtotal"
            symbol="%"
          />
        </Table.Col>
        <Table.Col />
      </Table.Row>
      <Table.Row>
        <Table.Col>
          <Table.Label label={t("design.buildingEfficiency")} variant="total" />
        </Table.Col>
        <Table.Col>
          <Table.Cell
            value={props.metrics.BuildingEfficiencyTotal * 100}
            precision={1}
            variant="total"
            symbol="%"
          />
        </Table.Col>
        <Table.Col />
      </Table.Row>
    </div>
  );
}

function useRows(
  metrics: ArchitecturalMetrics,
  t: (key: string, vars?: Record<string, string | number>) => string
) {
  return useMemo(
    () =>
      metrics.ResidentialEfficiencyPerLevel.map((resEff, index) => ({
        label: floorLabel(index, t),
        efficiency: resEff * 100,
        units: metrics.ApartmentQuantityPerLevel[index],
      })),
    [metrics.ResidentialEfficiencyPerLevel, metrics.ApartmentQuantityPerLevel, t]
  );
}
